import React, { useEffect, useState, MouseEvent, SyntheticEvent, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import { getBooleanOrDefault } from '../helpers';
import { updateFormControlValueAction, updateControlState } from '../actions/formActions';
import { IControlProps } from '../types/IFormSettings';
import { IOption } from '../types/IOption';
import { controlSelector } from '../selectors/formBuilder.selector';

const optionComparer = (item: string) => (option: IOption) => String(option.value) === String(item);

function DropDown({ controlName, formName }: IControlProps) {
    const [selectText, setSelectText] = useState('');
    const dispatch = useDispatch();
    const {
		value,
		readonly,
		options = [],
		multiple,
		buttonText = '',
		basicOptions = [],
		wcagAriaLabel,
	} = useSelector(controlSelector(formName, controlName))
	const currentValue = useRef(value)
	const [tabIdx, setTabIdx] = useState<number>(0)
	const getWcagAriaLabel =
		wcagAriaLabel && wcagAriaLabel.length > 0
			? wcagAriaLabel
			: 'Dropdown list with options'
	const [show, setShow] = useState<boolean>(false)

	useEffect(() => {
		currentValue.current = value
	}, [value])

	useEffect(() => {
		let selectTextNew = ''
		if (value.toString().includes(',')) {
			const array = value.split(',')
			const resultArray = array.reduce((array: any[], item: string) => {
				const option = options.find(optionComparer(item))
				return option ? array.concat([option.label]) : array
			}, [])

			selectTextNew = resultArray.join(', ') ?? buttonText
		} else {
			const option =
				options.find(optionComparer(value)) ||
				basicOptions.find(optionComparer(value))
			selectTextNew = option ? option.label : buttonText
		}

		setSelectText(selectTextNew)
	}, [value, options, buttonText, basicOptions])

	const getOptionClass = (v: string) => (isSelected(v) ? 'ticked' : '')

	const isSelected: (v: string) => boolean = (v: string) =>
		value.toString().includes(',')
			? value.split(',').some((x: any) => String(x) === String(v))
			: String(value) === String(v)

	const onMultipleSelect = (
		e: MouseEvent<HTMLLIElement> | React.KeyboardEvent,
	) => {
		e.preventDefault()
		const { value: dataValue = '' } = (e.target as HTMLElement).dataset
		const arrayValue: string[] = value !== '' ? value.split(',') : []

		const values =
			arrayValue.indexOf(dataValue) > -1
				? arrayValue.filter((v) => v !== dataValue)
				: arrayValue.concat([dataValue])

		dispatch(
			updateFormControlValueAction({
				formName,
				value: values.length > 0 ? values.toString() : '',
				controlName,
			}),
		)
	}

	const onSelect = (dataValue: string | null, e: SyntheticEvent<unknown>) => {
		e.preventDefault()

		const newSelectedValue = getBooleanOrDefault(dataValue ?? '')
		currentValue.current = newSelectedValue
	}

	const regKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			e.stopPropagation()

			if (multiple) {
				onMultipleSelect(e)
				return
			}

			const { value: dataValue = '' } = (e.target as HTMLElement).dataset
			dispatch(
				updateFormControlValueAction({
					formName,
					value: dataValue,
					controlName,
				}),
			)
			currentValue.current = getBooleanOrDefault(dataValue)
			onToggle(false)
		}
	}

	const onToggle = (isOpen: boolean) => {
		setShow(isOpen)
		const wrapperClass = isOpen ? 'active' : ''
		if (isOpen) setTabIndex()

		dispatch(
			updateControlState({
				formName,
				controlName,
				wrapperClass,
			}),
		)

		if (!isOpen) {
			dispatch(
				updateFormControlValueAction({
					formName,
					value: currentValue.current,
					controlName,
				}),
			)
		}
	}

	const checkOptions = options.length > 0 ? options : basicOptions

	const setTabIndex = () => {
		const getSearch = document.getElementById(controlName)
		if (!getSearch) return
		const getTabIndex = Number(getSearch.getAttribute('tabIndex'))
		setTabIdx(getTabIndex)
	}

	const optionMapMultiple = checkOptions.map(({ value: val, label }) => (
		<li
			key={`${val}-${label}`}
			onClick={onMultipleSelect}
			className={getOptionClass(val)}
			data-value={val}
			data-label={label}
			tabIndex={tabIdx}
			role="option"
			aria-selected={isSelected(val)}>
			{label || val}
		</li>
	))

	const optionMap = checkOptions.map(({ value: val, label }) => (
		<Dropdown.Item
			as="li"
			key={`${val}-${label}`}
			eventKey={val}
			data-value={val}
			className={getOptionClass(val)}
			tabIndex={tabIdx}
			role="option"
			aria-selected={isSelected(val)}>
			{label || val}
		</Dropdown.Item>
	))

	return (
		<Dropdown
			className="dropdown-extension"
			onSelect={onSelect}
			onToggle={onToggle}
			show={show}>
			<Dropdown.Toggle
				className="form-control"
				variant="default"
				id={controlName}
				disabled={readonly}
				value={value}>
				{selectText}
			</Dropdown.Toggle>

			<Dropdown.Menu
				as="ul"
				role="listbox"
				className="dropdown-extension"
				aria-label={getWcagAriaLabel}
				onKeyDown={regKeyDown}>
				{multiple ? optionMapMultiple : optionMap}
			</Dropdown.Menu>
		</Dropdown>
	)
}

export default DropDown;
